'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';


interface ServiceOption {
  id: string;
  name: string;
  basePrice: number;
}

interface HomeType {
  id: string;
  name: string;
  sqft: string;
  multiplier: number;
  specialMultiplier: number; // For gutter/roof/exterior services
}

interface ServiceCalculatorProps {
  services: ServiceOption[];
}

const homeTypes: HomeType[] = [
  { id: 'small-home', name: 'Small Home', sqft: '1,500-2,500 sq ft', multiplier: 1.0, specialMultiplier: 1.0 },
  { id: 'medium-home', name: 'Medium Home', sqft: '2,500-4,000 sq ft', multiplier: 1.4, specialMultiplier: 1.35 },
  { id: 'large-home', name: 'Large Home', sqft: '4,000-5,500 sq ft', multiplier: 1.96, specialMultiplier: 1.8225 },
  { id: 'xlarge-home', name: 'Extra Large Home', sqft: '5,500+ sq ft', multiplier: 2.744, specialMultiplier: 2.460375 },
];

export default function ServiceCalculator({ services }: ServiceCalculatorProps) {
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedHomeType, setSelectedHomeType] = useState<string>('medium-home');

  const toggleService = (serviceId: string) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const calculateTotal = () => {
    const homeType = homeTypes.find((ht) => ht.id === selectedHomeType);
    if (!homeType) return 0;

    // Services that use 35% increase
    const specialServices = ['gutter-cleaning', 'roof-cleaning', 'exterior-washing'];

    const total = services
      .filter((s) => selectedServices.includes(s.id))
      .reduce((sum, s) => {
        // Use specialMultiplier for gutter/roof/exterior, regular multiplier for others
        const multiplier = specialServices.includes(s.id) 
          ? homeType.specialMultiplier 
          : homeType.multiplier;
        return sum + (s.basePrice * multiplier);
      }, 0);

    return Math.round(total);
  };

  const getSelectedHomeType = () => {
    return homeTypes.find((ht) => ht.id === selectedHomeType);
  };

  return (
    <div className="bg-card rounded-lg shadow-brand p-6">
      <h3 className="font-serif text-2xl font-semibold text-foreground mb-6">
        Estimate Your Service Cost
      </h3>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-foreground mb-3">
            Select Home Type
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {homeTypes.map((homeType) => (
              <button
                key={homeType.id}
                onClick={() => setSelectedHomeType(homeType.id)}
                className={`relative flex flex-col items-center p-4 rounded-2xl border-2 transition-smooth btn-bubbly overflow-hidden ${
                  selectedHomeType === homeType.id
                    ? 'border-primary bg-accent shadow-lg' :'border-border hover:border-primary/50 hover:shadow-md'
                }`}
              >
                <div className="w-full h-24 mb-3 relative rounded-lg overflow-hidden flex items-center justify-center">
                  <svg
                    className="w-16 h-16 text-primary"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                  </svg>
                  {selectedHomeType === homeType.id && (
                    <div className="absolute top-2 right-2 bg-primary rounded-full p-1">
                      <Icon name="CheckCircleIcon" size={20} className="text-white" />
                    </div>
                  )}
                </div>
                <span className="font-semibold text-foreground text-sm text-center">{homeType.name}</span>
                <span className="text-xs text-muted-foreground mt-1 text-center">{homeType.sqft}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-3">
            Select Services
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() => toggleService(service.id)}
                className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-smooth btn-bubbly ${
                  selectedServices.includes(service.id)
                    ? 'border-primary bg-accent' :'border-border hover:border-primary/50'
                }`}
              >
                <span className="font-medium text-foreground text-sm">{service.name}</span>
                <Icon
                  name={selectedServices.includes(service.id) ? 'CheckCircleIcon' : 'PlusCircleIcon'}
                  size={24}
                  className={selectedServices.includes(service.id) ? 'text-primary' : 'text-muted-foreground'}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="bg-primary text-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-semibold">Estimated Total</span>
            <span className="text-3xl font-bold">${calculateTotal()}</span>
          </div>
          <p className="text-sm opacity-90 mb-4">
            {selectedServices.length === 0
              ? 'Select services to see your estimate'
              : `Based on ${selectedServices.length} service${selectedServices.length > 1 ? 's' : ''} for ${getSelectedHomeType()?.name} (${getSelectedHomeType()?.sqft})`}
          </p>
          <Link
            href="/get-quote"
            className="block w-full text-center px-6 py-3 bg-white text-primary font-semibold rounded-full hover:bg-accent transition-smooth btn-bubbly"
          >
            Get Accurate Quote
          </Link>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          * This is an estimate only. Final pricing may vary based on property condition and specific requirements.
        </p>
      </div>
    </div>
  );
}